import { useCallback, useRef } from "react"

import { useCore } from "@app/hooks/useCore"
import { getBrazeWhenReady, isBrazeDev, logSkipped } from "@app/lib/braze"
import { useConfigContext } from "@app/providers/config"
import { NormalisedCart } from "@root/types/cart"

const CUSTOMER_SESSION_KEY_PREFIX = "ab.shopify.shopify_customer_"
const CART_SESSION_KEY_PREFIX = "ab.shopify.shopify_cart_"
const SEARCH_INTENT_KEY = "ab.shopify.search_intent"
const SEARCH_LAST_FIRED_KEY = "ab.shopify.search_last_fired"

type ProductViewedProduct = {
  id: string
  title?: string | null
  productType?: string | null
  collections?: { nodes?: Array<{ title?: string | null } | null> | null } | Array<{ title?: string | null } | null> | null
  handle?: string | null
  images?: Array<{ originalSrc?: string | null } | null> | null
}

type ProductViewedVariant = {
  id: string
  price?: { amount?: string | null; currencyCode?: string | null } | null
  priceV2?: { amount?: string | null; currencyCode?: string | null } | null
  image?: { originalSrc?: string | null } | null
}

// Acquisition sources for the online_signup event. Every value except "shopify_account" is also a
// key in the SOURCE_TAGS allow-list in @app/api/customer-subscribe; "shopify_account" is
// client-only, since account registration goes through Shopify customerCreate, not that endpoint.
export type OnlineSignupSource = "footer" | "popup" | "shopify_account" | "back_in_stock" | "coming_soon" | "skin_quiz" | "competition_form"

export type CatalogSubscriptionType = "back_in_stock" | "price_drop"

type CatalogSubscriptionPayload = {
  id: string
  type: CatalogSubscriptionType[]
  catalogName: string
}

type ComingSoonSignupPayload = {
  productId: string
  productName?: string | null
  productHandle?: string | null
  imageUrl?: string | null
}

type ScentStackItem = {
  type: string
  variant_id: string
}

type IdentifyOptions = { logLoginEvent?: boolean }

export const useBraze = () => {
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const { store } = useConfigContext()
  const market = store?.locationRegion
  const source = store?.shopifyShopDomain
  const storeUrl = store?.url

  const lastProductViewedKeyRef = useRef<string | null>(null)
  const catalogSubscriptionKeysRef = useRef<Set<string>>(new Set())

  const identify = useCallback(
    async (customerId: string | number | null | undefined, options: IdentifyOptions = {}): Promise<void> => {
      if (typeof window === "undefined") return
      if (!customerId) return

      const id = String(customerId)
      const sessionKey = `${CUSTOMER_SESSION_KEY_PREFIX}${id}`
      if (window.sessionStorage.getItem(sessionKey)) return

      // Dev returns before the braze-ready await, so it has to reset the dedup state and write the
      // session key itself, otherwise dedup would be untestable locally.
      if (isBrazeDev) {
        logSkipped("changeUser", { customerId: id, market, logLoginEvent: options.logLoginEvent !== false, source })
        lastProductViewedKeyRef.current = null
        catalogSubscriptionKeysRef.current.clear()
        window.sessionStorage.setItem(sessionKey, id)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.changeUser(id)
      lastProductViewedKeyRef.current = null
      catalogSubscriptionKeysRef.current.clear()
      if (market) braze.getUser()?.setCustomUserAttribute("market", market)
      if (options.logLoginEvent !== false) braze.logCustomEvent("shopify_account_login", { source })
      braze.requestImmediateDataFlush()
      window.sessionStorage.setItem(sessionKey, id)
    },
    [market, source]
  )

  const onCartUpdate = useCallback(
    async (cart: NormalisedCart | null | undefined): Promise<void> => {
      if (typeof window === "undefined") return
      if (!cart?.id) return

      const cartToken = cart.id.split("/").pop()?.split("?key=")[0]
      if (!cartToken) return

      const sessionKey = `${CART_SESSION_KEY_PREFIX}${cartToken}`
      const needsAlias = !window.sessionStorage.getItem(sessionKey)

      const payload = {
        cart_id: cartToken,
        total_value: cart.cost?.totalAmount?.amount ? parseFloat(cart.cost.totalAmount.amount) : undefined,
        currency: cart.cost?.totalAmount?.currencyCode,
        source,
        products:
          cart.lines?.map(line => {
            const lineTotal = parseFloat(line.cost?.totalAmount?.amount ?? "0")
            const qty = line.quantity || 1
            return {
              product_id: decodeShopifyId(line.merchandise?.product?.id, "Product"),
              product_name: line.merchandise?.product?.title,
              variant_id: decodeShopifyId(line.merchandise?.id, "ProductVariant"),
              quantity: line.quantity,
              price: qty > 0 ? lineTotal / qty : 0,
              image_url: line.merchandise?.image?.originalSrc,
              product_url: line.merchandise?.product?.handle
                ? `https://${storeUrl}/products/${line.merchandise.product.handle}`
                : undefined,
            }
          }) ?? [],
        metadata: { total_quantity: cart.totalQuantity },
      }

      // As with identify, dev returns early so it writes the cart token key itself to keep the
      // once-per-session alias dedup testable locally.
      if (isBrazeDev) {
        if (needsAlias) {
          logSkipped("addAlias", { alias: "shopify_cart_token", label: `shopify_cart_${cartToken}` })
          window.sessionStorage.setItem(sessionKey, cartToken)
        }
        logSkipped("ecommerce.cart_updated", payload)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      if (needsAlias) {
        braze.getUser()?.addAlias("shopify_cart_token", `shopify_cart_${cartToken}`)
        braze.requestImmediateDataFlush()
        window.sessionStorage.setItem(sessionKey, cartToken)
      }

      braze.logCustomEvent("ecommerce.cart_updated", payload)
      braze.requestImmediateDataFlush()
    },
    [decodeShopifyId, source, storeUrl]
  )

  // `subscription` is the price drop catalog_subscription that QA pairs with every product_viewed.
  // It deliberately shares this function's dedup key and flush rather than going through
  // trackCatalogSubscription: that helper dedups on a per-instance Set keyed `id:type`, which
  // survives a variant switch and back (A -> B -> A), so the pair would diverge. Standalone
  // back_in_stock subscriptions still go through trackCatalogSubscription and keep that behaviour.
  const trackProductViewed = useCallback(
    async (
      product: ProductViewedProduct | null | undefined,
      variant?: ProductViewedVariant | null,
      subscription?: CatalogSubscriptionPayload | null
    ): Promise<void> => {
      if (typeof window === "undefined") return
      if (!product?.id) return

      const productId = decodeShopifyId(product.id, "Product")
      if (!productId) return

      const variantId = variant?.id ? decodeShopifyId(variant.id, "ProductVariant") : undefined

      const dedupKey = `${productId}:${variantId ?? ""}`
      if (lastProductViewedKeyRef.current === dedupKey) return
      lastProductViewedKeyRef.current = dedupKey

      const price = variant?.price?.amount ?? variant?.priceV2?.amount
      const currency = variant?.price?.currencyCode ?? variant?.priceV2?.currencyCode

      const collections = product.collections as
        | { nodes?: Array<{ title?: string | null } | null> | null }
        | Array<{ title?: string | null } | null>
        | null
        | undefined
      const category = product.productType || (Array.isArray(collections) ? collections?.[0]?.title : collections?.nodes?.[0]?.title)

      const image_url = variant?.image?.originalSrc ?? product.images?.[0]?.originalSrc ?? undefined
      const product_url = product.handle && storeUrl ? `https://${storeUrl}/products/${product.handle}` : undefined

      const payload = {
        product_id: productId,
        product_name: product.title,
        variant_id: variantId,
        price: price ? parseFloat(price) : undefined,
        currency,
        source,
        image_url,
        product_url,
        metadata: { category },
      }

      const subscriptionPayload =
        subscription?.id && subscription.type?.length && subscription.catalogName
          ? { id: subscription.id, catalog_name: subscription.catalogName, type: subscription.type }
          : null

      if (isBrazeDev) {
        logSkipped("ecommerce.product_viewed", payload)
        if (subscriptionPayload) logSkipped("catalog_subscription", subscriptionPayload)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("ecommerce.product_viewed", payload)
      if (subscriptionPayload) braze.logCustomEvent("catalog_subscription", subscriptionPayload)
      // One flush drains the whole queued batch, so both events go out in a single request.
      braze.requestImmediateDataFlush()
    },
    [decodeShopifyId, source, storeUrl]
  )

  const trackCategoryViewed = useCallback(
    async ({ category_name, category_url }: { category_name: string; category_url: string }): Promise<void> => {
      if (typeof window === "undefined") return
      if (!category_name) return

      const payload = { product_type: category_name, category_url }

      if (isBrazeDev) {
        logSkipped("category_viewed", payload)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("category_viewed", payload)
    },
    []
  )

  const trackSearchPerformed = useCallback(
    async ({ search_term, results_count }: { search_term: string; results_count: number }): Promise<void> => {
      if (typeof window === "undefined") return
      if (!search_term) return

      const dedupKey = `${search_term}:${results_count}`
      const intent = window.sessionStorage.getItem(SEARCH_INTENT_KEY)

      let firedKeys: string[] = []
      try {
        const raw = window.sessionStorage.getItem(SEARCH_LAST_FIRED_KEY)
        if (raw) firedKeys = JSON.parse(raw)
        if (!Array.isArray(firedKeys)) firedKeys = []
      } catch {
        firedKeys = []
      }

      // Intent token bypasses the fired set so genuine resubmits always fire, even for a term already in the set.
      // Without intent (passive: back/forward, refresh, suggested-term re-click), suppress if already fired this session.
      // Note: the token lingers if a handler resubmits the same term while already on its results page (effect deps don't
      // change, so it isn't consumed); a later back/forward can then consume it and fire once. Accepted trade-off.
      if (intent === search_term) {
        window.sessionStorage.removeItem(SEARCH_INTENT_KEY)
      } else if (firedKeys.includes(dedupKey)) {
        return
      }

      if (!firedKeys.includes(dedupKey)) {
        firedKeys.push(dedupKey)
        window.sessionStorage.setItem(SEARCH_LAST_FIRED_KEY, JSON.stringify(firedKeys))
      }

      const payload = { search_term, results_count }

      if (isBrazeDev) {
        logSkipped("search_performed", payload)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("search_performed", payload)
    },
    []
  )

  const trackOnlineSignup = useCallback(
    async ({
      source,
      campaign,
      scentStack,
    }: {
      source: OnlineSignupSource
      campaign?: string
      scentStack?: ScentStackItem[]
    }): Promise<void> => {
      if (typeof window === "undefined") return
      if (!source) return

      const payload = { source, ...(campaign ? { campaign } : {}) }

      if (isBrazeDev) {
        logSkipped("online_signup", payload)
        if (scentStack?.length) logSkipped("setCustomUserAttribute", { scent_stack: scentStack })
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("online_signup", payload)
      if (scentStack?.length) braze.getUser()?.setCustomUserAttribute("scent_stack", scentStack)

      braze.requestImmediateDataFlush()
    },
    []
  )

  const trackCatalogSubscription = useCallback(async ({ id, type, catalogName }: CatalogSubscriptionPayload): Promise<void> => {
    if (typeof window === "undefined") return
    if (!id || !type) return
    if (!catalogName) return

    const dedupKey = `${id}:${[...type].sort().join(",")}`
    if (catalogSubscriptionKeysRef.current.has(dedupKey)) return
    catalogSubscriptionKeysRef.current.add(dedupKey)

    const payload = { id, catalog_name: catalogName, type }

    if (isBrazeDev) {
      logSkipped("catalog_subscription", payload)
      return
    }

    const braze = await getBrazeWhenReady()
    if (!braze) return

    braze.logCustomEvent("catalog_subscription", payload)
    braze.requestImmediateDataFlush()
  }, [])

  const trackComingSoonSignup = useCallback(
    async ({ productId, productName, productHandle, imageUrl }: ComingSoonSignupPayload): Promise<void> => {
      if (typeof window === "undefined") return
      if (!productId) return

      const payload = {
        product_id: productId,
        product_name: productName,
        product_url: productHandle && storeUrl ? `https://${storeUrl}/products/${productHandle}` : undefined,
        product_image_url: imageUrl,
      }

      if (isBrazeDev) {
        logSkipped("coming_soon_signup", payload)
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("coming_soon_signup", payload)
      braze.requestImmediateDataFlush()
    },
    [storeUrl]
  )

  const trackSkinQuizCompleted = useCallback(
    async (result: string | null | undefined, recommendedVariantIds: string[] = []): Promise<void> => {
      if (typeof window === "undefined") return
      if (!result) return

      if (isBrazeDev) {
        logSkipped("skin_quiz_completed", { skin_quiz_result: result, recommended_variant_ids: recommendedVariantIds })
        return
      }

      const braze = await getBrazeWhenReady()
      if (!braze) return

      braze.logCustomEvent("skin_quiz_completed", { skin_quiz_result: result })
      braze.getUser()?.setCustomUserAttribute("skin_quiz_result", [{ result, recommended_variant_ids: recommendedVariantIds }])
      braze.requestImmediateDataFlush()
    },
    []
  )

  const logoutCleanup = useCallback(async (): Promise<void> => {
    if (typeof window === "undefined") return

    if (isBrazeDev) {
      logSkipped("wipeData")
    } else {
      const braze = await getBrazeWhenReady()
      if (braze) {
        braze.wipeData()
        braze.openSession()
      }
    }

    // The session key sweep runs in dev too, so the identify/cart dedup keys clear on logout.
    Object.keys(window.sessionStorage)
      .filter(
        key =>
          key.startsWith(CUSTOMER_SESSION_KEY_PREFIX) ||
          key.startsWith(CART_SESSION_KEY_PREFIX) ||
          key === SEARCH_INTENT_KEY ||
          key === SEARCH_LAST_FIRED_KEY
      )
      .forEach(key => window.sessionStorage.removeItem(key))
  }, [])

  return {
    identify,
    onCartUpdate,
    trackProductViewed,
    trackCategoryViewed,
    trackSearchPerformed,
    trackOnlineSignup,
    trackCatalogSubscription,
    trackComingSoonSignup,
    trackSkinQuizCompleted,
    logoutCleanup,
  }
}
